/**
 * DeepRhythm, the optional trained tempo detector, in two places:
 *
 * - **Settings → Library**: one switch, "Use DeepRhythm for BPM". Turning it on
 *   downloads it the first time (torch and the model, nova/extras.py), with a
 *   progress bar. It applies to songs analysed from then on.
 * - **Settings → BPM comparison**: songs picked from the library (right-click →
 *   Re-analyse → Compare BPM) measured both ways. Listen, then say which was
 *   right; the tally is the answer to "is it better on my music?". Nothing
 *   here changes a song's BPM.
 */

import { useCallback, useEffect, useState } from "react";
import {
  clearBpmComparison,
  compareBpm,
  checkDeepRhythm,
  downloadDeepRhythm,
  pauseJobs,
  dumpAnalysis,
  fetchAnalysisHealth,
  restartAnalysis,
  type AnalysisHealth,
  fetchAnalysisLog,
  fetchBpmComparison,
  fetchDeepRhythm,
  fetchTracksByIds,
  removeDeepRhythm,
  setBpmVerdict,
  setDeepRhythm,
  type BpmCompareResult,
  type BpmComparison,
  type DeepRhythmStatus,
} from "../api";
import { useApp, type AppState } from "../core/app";
import { bus } from "../core/bus";
import { player } from "../core/player";
import type { Track } from "../types";
import { toast } from "./Toast";

const mb = (bytes: number) => `${(bytes / 1048576).toFixed(0)} MB`;

function useDeepRhythm(offline: boolean) {
  const [status, setStatus] = useState<DeepRhythmStatus | null>(null);
  const read = useCallback(() => void fetchDeepRhythm().then((s) => s && setStatus(s)), []);
  useEffect(() => {
    if (offline) return;
    read();
    return bus.on("deeprhythm.changed", read);
  }, [offline, read]);
  const busy = status?.download.phase === "downloading" || status?.download.phase === "checking";
  useEffect(() => {
    if (!busy) return;
    const t = window.setInterval(read, 1000);
    return () => window.clearInterval(t);
  }, [busy, read]);
  return { status, setStatus, read };
}

/** The row in Settings → Library. */
export function DeepRhythmPanel() {
  const app = useApp();
  const { status, setStatus } = useDeepRhythm(app.offline);
  if (!status) return null;
  const d = status.download;
  const downloading = d.phase === "downloading" || d.phase === "checking";
  const check = status.installed?.check;
  const on = status.setting !== false && (status.active || downloading || status.setting === true);
  const toggle = async (enabled: boolean) => {
    const next = await setDeepRhythm(enabled);
    if (next) setStatus(next);
  };
  return (
    <div className={`nv-update ${d.phase === "failed" ? "failed" : status.active ? "ready" : ""}`}>
      <div className="nv-update-head nv-dr-head">
        <b>Use DeepRhythm for BPM</b>
        <label className="nv-toggle">
          <input type="checkbox" role="switch" aria-label="Use DeepRhythm for BPM" checked={on} disabled={app.offline || downloading}
            onChange={(e) => void toggle(e.target.checked)} />
          <i aria-hidden="true" />
        </label>
      </div>
      <span className="nv-dim">
        A trained tempo detector that reads the audio alone. It is right more often on the cross-rhythms Afrobeats is
        built on, where Nova's own detector can read 4/3 of the tempo. It applies to songs analysed from now on; use
        Compare BPM on a few songs first to hear the difference.
        {!status.importable && ` Turning it on downloads it once (${status.sizeHint}).`}
      </span>
      {downloading && (
        <>
          <div className="nv-update-bar" aria-label="Download progress"><i className="unknown" style={{ width: "100%" }} /></div>
          <span className="nv-dim tnum">
            {d.phase === "checking"
              ? "Checking that it loads on this computer…"
              : `${mb(d.downloaded)} · ${d.current ?? "starting"} (file ${d.files})`}
          </span>
        </>
      )}
      {d.phase === "failed" && <span className="nv-ffmpeg-error">{d.error}</span>}
      {d.phase !== "failed" && !downloading && check && !check.ok && (
        <span className="nv-ffmpeg-error">Downloaded, but it would not load on this computer: {check.error}</span>
      )}
      {!downloading && check?.ok && <span className="nv-dim">Loads and runs on this computer (checked {check.checkedAt.slice(0, 10)}).</span>}
      <div className="nv-update-actions">
        {d.phase === "failed" && !status.installed && <button className="nv-btn sm" onClick={() => void downloadDeepRhythm().then((s) => s && setStatus(s))}>Try again</button>}
        {status.installed && !downloading && (d.phase === "failed" || (check && !check.ok)) && (
          <button className="nv-btn sm" onClick={() => void checkDeepRhythm().then((s) => s && setStatus(s))}>Check again</button>
        )}
        <button className="nv-btn sm" onClick={() => app.openSettings("bpm-compare")}>BPM comparison…</button>
        {status.installed && !downloading && (
          <button className="nv-btn sm" onClick={() => void removeDeepRhythm().then((s) => s && setStatus(s))}
            title={`Installed ${status.installed.installedAt.slice(0, 10)}: torch ${status.installed.packages.torch ?? ""}`}>
            Remove download ({mb(status.installed.bytes)})
          </button>
        )}
      </div>
    </div>
  );
}

/** Right-click → Re-analyse → Compare BPM. */
export async function startBpmComparison(app: AppState, tracks: Track[]) {
  const res = await compareBpm(tracks.map((t) => t.id));
  if (!res) {
    toast("Could not reach the engine.", "bad");
    return;
  }
  if (res.error) {
    toast(res.error, "bad");
    app.openSettings("analysis");
    return;
  }
  app.openSettings("bpm-compare");
}

const fmt = (bpm: number | null) => (bpm == null ? "–" : bpm.toFixed(1));

function Verdict({ song, onChange }: { song: BpmComparison; onChange: () => void }) {
  const [typed, setTyped] = useState(song.trueBpm != null ? String(song.trueBpm) : "");
  const set = async (verdict: BpmComparison["verdict"]) => {
    const n = typed.trim() ? Number(typed) : null;
    const res = await setBpmVerdict(song.audioSha256, verdict, n != null && Number.isFinite(n) ? n : null);
    if (res?.error) toast(res.error, "bad");
    onChange();
  };
  const pick = (v: NonNullable<BpmComparison["verdict"]>, label: string) => (
    <button className={`nv-btn sm ${song.verdict === v ? "primary" : ""}`} aria-pressed={song.verdict === v}
      onClick={() => void set(song.verdict === v ? null : v)}>{label}</button>
  );
  return (
    <div className="nv-dr-verdict">
      {song.agree ? pick("both", "Both right") : <>{pick("nova", "Nova")}{pick("deeprhythm", "DeepRhythm")}</>}
      {pick("neither", "Neither")}
      {song.verdict === "neither" && (
        <input className="nv-input sm" inputMode="decimal" placeholder="Real BPM" aria-label="Real BPM" value={typed}
          onChange={(e) => setTyped(e.target.value)} onBlur={() => void set("neither")} style={{ width: 80 }} />
      )}
    </div>
  );
}

const duration = (seconds: number) =>
  seconds < 60 ? `${Math.max(1, Math.round(seconds))} s` : `${Math.round(seconds / 60)} min`;

/** How far the comparison has got, and why it is waiting when it is. */
function Progress({ progress: p, onResume, stuck }: { progress: BpmCompareResult["progress"]; onResume: () => void; stuck: boolean }) {
  const finished = p.done + p.failed;
  const left = p.total - finished;
  const pct = p.total ? Math.round((100 * finished) / p.total) : 0;
  let line: string;
  if (p.waitingReason === "paused") line = "Analysis is paused, so the comparison is waiting.";
  else if (p.waitingReason) line = p.waitingReason;
  else if (stuck) line = "One song looks stuck: see below.";
  else if (p.measuring) {
    line = `Measuring song ${finished + 1} of ${p.total}`;
    if (p.secondsPerSong) line += ` · about ${duration(p.secondsPerSong * left)} left`;
    else line += " · the first song takes longer while DeepRhythm loads";
  } else line = `Waiting for the analysis process to start (${left} to measure)`;
  return (
    <div className={`nv-update ${p.waitingReason ? "failed" : ""}`} role="status" aria-live="polite">
      <div className="nv-update-head">
        <b className="tnum">{finished} of {p.total} measured</b>
        <span className="nv-dim">{line}</span>
      </div>
      <div className="nv-update-bar" aria-label="Comparison progress">
        <i style={{ width: `${Math.max(pct, 3)}%` }} className={p.measuring && !finished ? "unknown" : ""} />
      </div>
      {p.waitingReason === "paused" && (
        <div className="nv-update-actions"><button className="nv-btn sm primary" onClick={onResume}>Resume analysis</button></div>
      )}
    </div>
  );
}

/** One stage of one song should take seconds; past this it is called stuck. */
const STUCK_SECONDS = 90;

/** When a song is stuck, or failed: say where, and show the analysis log. */
function Stuck({ songs, idle, onRestarted }: { songs: BpmComparison[]; idle: boolean; onRestarted: () => void }) {
  const stuck = songs.find((s) => s.status === "measuring" && (s.stepSeconds ?? 0) >= STUCK_SECONDS);
  const failed = songs.find((s) => s.status === "failed");
  const [log, setLog] = useState<{ path: string | null; lines: string[] } | null>(null);
  const [loading, setLoading] = useState(false);
  const [health, setHealth] = useState<AnalysisHealth | null>(null);
  const [restarting, setRestarting] = useState(false);
  const trouble = !!stuck || idle;
  useEffect(() => {
    if (!trouble) return;
    void fetchAnalysisHealth().then(setHealth);
    const t = window.setInterval(() => void fetchAnalysisHealth().then(setHealth), 5000);
    return () => window.clearInterval(t);
  }, [trouble]);
  if (!stuck && !failed && !idle && !log) return null;
  const restart = async () => {
    setRestarting(true);
    const res = await restartAnalysis();
    setRestarting(false);
    if (res?.error) toast(res.error, "bad");
    else {
      toast("Analysis restarted.", "ok");
      if (res) setHealth(res);
      onRestarted();
    }
  };
  const show = async () => {
    setLoading(true);
    if (stuck) {
      await dumpAnalysis();
      await new Promise((r) => setTimeout(r, 1500));
    }
    setLog(await fetchAnalysisLog());
    setLoading(false);
  };
  const text = log?.lines.join("\n") ?? "";
  return (
    <div className="nv-update failed" role="alert">
      <div className="nv-update-head">
        {stuck ? (
          <>
            <b>“{stuck.title}” looks stuck.</b>
            <span className="nv-dim">It has been at “{stuck.step}” for {duration(stuck.stepSeconds ?? 0)}; that should take seconds.</span>
          </>
        ) : idle ? (
          <>
            <b>Nothing has started for over a minute.</b>
            <span className="nv-dim">The songs are queued, but the analysis process has not picked them up.</span>
          </>
        ) : failed ? (
          <>
            <b>“{failed.title}” could not be compared.</b>
            <span className="nv-dim">{failed.error}</span>
          </>
        ) : (
          <b>Analysis log</b>
        )}
      </div>
      {trouble && health && <span className="nv-dim tnum">{describeHealth(health)}</span>}
      <div className="nv-update-actions">
        {trouble && (
          <button className="nv-btn sm primary" disabled={restarting} onClick={() => void restart()}>
            {restarting ? "Restarting…" : "Restart analysis"}
          </button>
        )}
        <button className="nv-btn sm" disabled={loading} onClick={() => void show()}>
          {loading ? "Reading the log…" : log ? "Read the log again" : "Show details"}
        </button>
        {log && log.lines.length > 0 && (
          <button className="nv-btn sm" onClick={() => void navigator.clipboard?.writeText(text).then(() => toast("Copied the log.", "ok"))}>Copy</button>
        )}
      </div>
      {log && (
        <>
          <span className="nv-dim">{log.path ? `From ${log.path}. Send this to whoever helps you with Nova.` : "No log yet."}</span>
          <pre className="nv-dr-log">{text || "The log is empty."}</pre>
        </>
      )}
    </div>
  );
}

/** One line on what the analysis process is doing, for the person and for a bug report. */
export function describeHealth(h: AnalysisHealth): string {
  if (!h.ffmpegReady) return "Analysis has not started: ffmpeg is missing.";
  if (h.paused) return "Analysis is paused.";
  if (h.process.broken) {
    const last = [...h.process.broken.lines].reverse().find((l) => l.trim()) ?? "no message";
    return `Analysis keeps stopping (exit code ${h.process.broken.code ?? "?"}): ${last}`;
  }
  const proc = h.process.inProcess
    ? "Analysis runs inside the engine."
    : h.process.running
      ? `Analysis process running (pid ${h.process.pid ?? "?"}).`
      : h.process.asleep ? "Analysis process asleep (no work)." : "Analysis process is not running.";
  const on = h.running.length
    ? ` Working on: ${h.running.map((r) => `${r.kind}${r.song ? ` (${r.song})` : ""} for ${duration(r.seconds)}`).join("; ")}.`
    : " Working on nothing.";
  return `${proc}${on} ${h.queued} queued.`;
}

/** Settings → BPM comparison. */
export function BpmComparePage() {
  const app = useApp();
  const [data, setData] = useState<BpmCompareResult | null>(null);
  const read = useCallback(() => void fetchBpmComparison().then((r) => r && setData(r)), []);
  useEffect(() => {
    read();
    const offs = [bus.on("analysis.progress", read), bus.on("deeprhythm.changed", read)];
    return () => offs.forEach((off) => off());
  }, [read]);
  const waiting = data?.songs.some((s) => s.status === "queued" || s.status === "measuring");
  useEffect(() => {
    if (!waiting) return;
    const t = window.setInterval(read, 2000);
    return () => window.clearInterval(t);
  }, [waiting, read]);

  // Waiting with nothing measuring and no reason given: how long has it been?
  const [idleSince, setIdleSince] = useState<number | null>(null);
  const nothingMoving = !!data && data.progress.waiting > 0 && data.progress.measuring === 0 && !data.progress.waitingReason;
  useEffect(() => {
    setIdleSince((since) => (nothingMoving ? since ?? Date.now() : null));
  }, [nothingMoving]);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (idleSince == null) return;
    const t = window.setInterval(() => setNow(Date.now()), 5000);
    return () => window.clearInterval(t);
  }, [idleSince]);
  const idle = idleSince != null && now - idleSince > 60_000;

  const play = async (song: BpmComparison) => {
    if (!song.trackId) return;
    const [track] = await fetchTracksByIds([song.trackId]);
    if (track) player.playTracks([track]);
  };

  if (!data) return <p className="nv-dim">Loading…</p>;
  const t = data.tally;
  const p = data.progress;
  return (
    <div className="nv-dr-page">
      <p className="nv-dim">
        Pick songs in the library, right-click → <b>Re-analyse → Compare BPM</b>. Each is measured by Nova's own detector
        and by Nova with DeepRhythm deciding. Play the ones they disagree on and say which is right: the tally below is
        then measured on your music, by your ear. The file's own BPM tag is shown for reference only — it is not
        trusted. Nothing here changes any song's BPM.
      </p>
      <div className="nv-dr-tally" role="status">
        <span><b className="tnum">{t.compared}</b> compared</span>
        <span><b className="tnum">{t.agree}</b> agree (within {data.agreePercent}%)</span>
        <span><b className="tnum">{t.differ}</b> differ</span>
        <span>You checked <b className="tnum">{t.checked}</b>: Nova right <b className="tnum">{t.novaRight}</b>, DeepRhythm right <b className="tnum">{t.deepRight}</b></span>
      </div>
      {p.done + p.failed < p.total && <Progress progress={p} onResume={() => void pauseJobs(false).then(read)}
        stuck={data.songs.some((s) => s.status === "measuring" && (s.stepSeconds ?? 0) >= STUCK_SECONDS)} />}
      <Stuck songs={data.songs} idle={idle} onRestarted={read} />
      {data.songs.length === 0 ? (
        <p className="nv-dim">No songs compared yet.</p>
      ) : (
        <div className="nv-dr-scroll">
        <table className="nv-table nv-dr-table">
          <thead>
            <tr><th style={{ width: 36 }} /><th>Song</th><th className="num">Nova</th><th className="num">DeepRhythm</th><th className="num">Tag</th><th>Which is right?</th></tr>
          </thead>
          <tbody>
            {data.songs.map((s) => (
              <tr key={s.audioSha256} className={s.status === "done" && !s.agree ? "differ" : ""}>
                <td><button className="nv-iconbtn" aria-label={`Play ${s.title}`} disabled={!s.trackId} onClick={() => void play(s)}>▶</button></td>
                <td title={s.error ?? undefined}>
                  <b>{s.title}</b>{s.artist ? <span className="nv-dim"> — {s.artist}</span> : null}
                  {s.status === "failed" && <div className="nv-ffmpeg-error" title={s.error ?? undefined}>could not be compared</div>}
                </td>
                {s.status === "queued" || s.status === "measuring" ? (
                  <td colSpan={2} className="num nv-dim">
                    {s.status === "measuring"
                      ? <span className="nv-dr-measuring">{s.step ?? "measuring"}…{s.stepSeconds != null && s.stepSeconds >= 3 ? ` ${duration(s.stepSeconds)}` : ""}</span>
                      : "waiting"}
                  </td>
                ) : (
                  <>
                    <td className="num tnum">{fmt(s.novaBpm)}</td>
                    <td className="num tnum">{fmt(s.deepBpm)}</td>
                  </>
                )}
                <td className="num tnum nv-dim">{fmt(s.tagBpm)}</td>
                <td>{s.status === "done" ? <Verdict song={s} onChange={read} /> : null}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
      {data.songs.length > 0 && (
        <div className="nv-update-actions" style={{ marginTop: 12 }}>
          <button className="nv-btn sm" onClick={() => { if (window.confirm("Forget every comparison and your answers?")) void clearBpmComparison().then(read); }}>
            Clear the comparison
          </button>
        </div>
      )}
      {app.offline && <p className="nv-dim">The engine is not running.</p>}
    </div>
  );
}
